import { useApolloClient, useQuery } from "@apollo/client";
import { FormProvider } from "contexts/FormContext";
import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import updateData from "gql/dashboard/sales/updateData.gql";
import draftgql from "gql/dashboard/sales/draftOrders.gql";
import { useAppContext } from "contexts/AppContext";
import SalesOrderForm from "./SalesOrderForm";
import useForm from "hooks/useForm";
import { toast } from "react-toastify";
import humps from "humps";
import itemMasterDatagql from "gql/dashboard/common/itemMasterData.gql";
import useResources from "hooks/useResources";
import useAxios from "hooks/useAxios";

const Store = () => {
  // constants
  const DRAFT_INTERVAL = 60; // seconds
  const RETRY_FAILURE_INTERVAL = 10; //seconds
  const FAILURE_FATIGUE = 3; // times after it fails in order to take action

  // contexts
  const { appState, setAppState } = useAppContext();

  // states
  const [updateLoading, setUpdateLoading] = useState(false);
  const [draftInterval, setDraftInterval] = useState(null);
  const [failedCounter, setFailedCounter] = useState(0);
  const [cId, setCId] = useState(null);
  const [eId, setEId] = useState(null);
  const draftIntervalRef = useRef(null);
  const cIdRef = useRef(null);
  const eIdRef = useRef(null);

  // hooks
  const { id } = useParams();
  const { makeItemMasterData } = useResources();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading, data, error, post, put } = useAxios();
  const storeProps = useForm(
    {
      settings: {
        viewMode: localStorage.getItem("viewMode") || "item",
      },
      update: false,
      details: [],
      recentlyRemoved: [],
      levelHistory: {},
      info: {
        salesDate: new Date().toISOString().split("T")[0],
        deliveryDate: new Date().toISOString().split("T")[0],
        salesMan: appState.auth.user,
        customer: searchParams.get("customer") && {
          value: appState.resources.customers.find(
            (x) => x.id == searchParams.get("customer")
          ),
          label: appState.resources.customers.find(
            (x) => x.id == searchParams.get("customer")
          ).name,
        },
        customerBranch: null,
        branches: [],
        customerEmail: "",
        customerPoNumber: "",
        paymentTerms: null,
        customerSignature: null,
        salesmanSignature: null,
        remarks: "",
        terms: "",
        labeling: "",
      },
    },
    id || searchParams.get("d-id") ? null : "temp-order"
  );
  const formStateRef = useRef(storeProps.formState);
  const apollo = useApolloClient();
  const {
    loading: resourceLoading,
    data: resourceData,
    error: resourceError,
  } = useQuery(itemMasterDatagql, {
    skip: !appState.settings.internet.online,
  });

  // functions
  // drafts order
  const draftIt = (d = null, offline = false) => {
    let formState = d || formStateRef.current;

    const data = {
      id: formState.info.id,
      sales_man: formState.info?.salesMan?.id,
      doc_date: formState.info?.salesDate,
      deliveryDate: formState.info?.deliveryDate,
      customer: formState.info?.customer?.value?.id,
      customer_branch: formState.info?.customerBranch?.value?.id,
      customer_email: formState.info?.customerEmail,
      customerOrderNumber: formState.info?.customerPONumber,
      paymentTerms: formState.info?.paymentTerms?.value?.id,
      deliveryPoint: formState.info?.customerBranch?.value?.address,
      terms: formState.info?.terms,
      labeling: formState.info?.labeling,
      remarks: formState.info?.remarks,
      customer_signature: formState.info?.customerSignature,
      sales_man_signature: formState.info?.salesmanSignature,
      details: formState.details
        .filter((x) => x.item?.id)
        .map((cartItem) => ({
          id: cartItem.id,
          deliveryDate: formState.info?.deliveryDate,
          item: cartItem.item?.id,
          quantity: cartItem.qty,
          deliveryPoint: formState.info?.customerBranch?.value.address,
          sales_price: cartItem.price || 0,
          extra: !!cartItem.extra,
        })),
    };

    if (
      !offline &&
      appState.settings?.internet?.online &&
      !searchParams.get("e-id")
    ) {
      if (cIdRef.current) {
        put(`/sales/salesorders/draft/?uuid=${cIdRef.current}`, data);
      } else {
        post("/sales/salesorders/draft/", data);
      }
    } else {
      if (eIdRef.current) {
        let local_drafts = JSON.parse(localStorage.getItem("drafts") || "[]");
        local_drafts = local_drafts?.map((doc) => {
          return doc.eId != eIdRef.current
            ? doc
            : { ...doc, ...data, updatedAt: new Date() };
        });
        localStorage.setItem("drafts", JSON.stringify(local_drafts));
      } else {
        const eid = Math.random();
        setSearchParams({ "e-id": eid });
        setEId(eid);
        eIdRef.current = eid;
        const local_drafts = [
          ...JSON.parse(localStorage.getItem("drafts") || "[]"),
          { ...data, eId: eid, createdAt: new Date(), updatedAt: new Date() },
        ];
        localStorage.setItem("drafts", JSON.stringify(local_drafts));
      }
    }
  };

  // firing draft function periodically
  const periodicDraft = () => {
    if (draftInterval) clearInterval(draftInterval);
    setDraftInterval(
      setInterval(() => {
        draftIt();
      }, DRAFT_INTERVAL * 1000)
    );
  };

  // stop draft interval and draft the order one more time and removing window event listener
  const cleanUp = () => {
    clearInterval(draftIntervalRef.current);
    draftIt();
    window.removeEventListener("beforeunload", cleanUp);
  };

  // effects
  // setting the latest resources
  useEffect(() => {
    storeProps.setFormState({
      key: "customers",
      value: appState.resources.customers,
    });
    storeProps.setFormState({
      key: "users",
      value: appState.resources.users,
    });
    storeProps.setFormState({
      key: "paymentTerms",
      value: appState.resources.paymentTerms,
    });
    storeProps.setFormState({
      key: "allItems",
      value: appState.resources.salesItems,
    });
  }, []);

  // handle update procedures and drafting the order
  useEffect(() => {
    if (searchParams.get("e-id")) {
      if (id) {
        storeProps.setFormState({
          key: "update",
          value: true,
        });
      }

      const so = JSON.parse(localStorage.getItem("drafts") || "[]").find(
        (x) => x.eId == searchParams.get("e-id")
      );

      so.customer = appState.resources.customers.find(
        (x) => x.id == so.customer
      );

      so.customerBranch = so.customer?.branches.find(
        (x) => x.id == so.customer_branch
      );

      so.details.forEach((detail) => {
        const item = detail.item;
        detail.item = appState.resources.items.find((x) => x.id == item);
        if (!detail.item) {
          toast.warning(
            <p>
              Item <b>{item?.displayName}</b> is inactive. It is removed from
              your cart.
            </p>
          );
        }
      });

      storeProps.setFormState({
        batch: [
          {
            key: "info",
            value: {
              id,
              salesDate: so?.doc_dateو,
              deliveryDate: so.deliveryDate,
              salesMan: appState.auth.user,
              customer: so.customer
                ? { value: so.customer, label: so.customer.name }
                : null,
              customerBranch: so.customerBranch
                ? {
                  value: so.customerBranch,
                  label: so.customerBranch?.name,
                }
                : null,
              customerEmail: so.customer_email,
              customerPONumber: so.customerOrderNumber,
              paymentTerms: so.paymentTerms
                ? {
                  value: appState.resources.paymentTerms.find(
                    (x) => x.id == so.paymentTerms
                  ),
                  label: appState.resources.paymentTerms.find(
                    (x) => x.id == so.paymentTerms
                  )?.name,
                }
                : null,
              customerSignature: so.customer_signature,
              salesmanSignature: so.sales_man_signature,
              remarks: so.remarks,
              terms: so.terms,
              labeling: so.labeling,
            },
          },
          {
            key: "details",
            value: so.details
              .filter((detail) => !!detail.item)
              .map((cartItem) => ({
                uid: Math.random(),
                id: cartItem.id,
                item: cartItem.item,
                price: cartItem.sales_price,
                qty: cartItem.quantity,
                extra: cartItem.extra,
              })),
          },
        ],
      });

      setEId(searchParams.get("e-id"));
      eIdRef.current = searchParams.get("e-id");
    } else if (searchParams.get("c-id")) {
      setUpdateLoading(true);
      if (id) {
        storeProps.setFormState({
          key: "update",
          value: true,
        });
      }
      apollo
        .query({
          query: draftgql,
          variables: { uuid: searchParams.get("c-id") },
        })
        .then((data) => {
          const so = data.data.draftedDocuments[0].data;

          so.customer = appState.resources.customers.find(
            (x) => x.id == so.customer
          );

          so.customerBranch = so.customer?.branches.find(
            (x) => x.id == so.customer_branch
          );

          so.details.forEach((detail) => {
            const item = detail.item;
            detail.item = appState.resources.items.find((x) => x.id == item);
            if (!detail.item) {
              toast.warning(
                <p>
                  Item <b>{item?.displayName}</b> is inactive. It is removed
                  from your cart.
                </p>
              );
            }
          });

          storeProps.setFormState({
            batch: [
              {
                key: "info",
                value: {
                  id,
                  salesDate: so.doc_date,
                  deliveryDate: so.deliveryDate,
                  salesMan: appState.auth.user,
                  customer: so.customer
                    ? { value: so.customer, label: so.customer.name }
                    : null,
                  customerBranch: so.customerBranch
                    ? {
                      value: so.customerBranch,
                      label: so.customerBranch?.name,
                    }
                    : null,
                  customerEmail: so.customer_email,
                  customerPONumber: so.customerOrderNumber,
                  paymentTerms: so.paymentTerms
                    ? {
                      value: appState.resources.paymentTerms.find(
                        (x) => x.id == so.paymentTerms
                      ),
                      label: appState.resources.paymentTerms.find(
                        (x) => x.id == so.paymentTerms
                      )?.name,
                    }
                    : null,
                  customerSignature: so.customer_signature,
                  salesmanSignature: so.sales_man_signature,
                  remarks: so.remarks,
                  terms: so.terms,
                  labeling: so.labeling,
                },
              },
              {
                key: "details",
                value: so.details
                  .filter((detail) => !!detail.item)
                  .map((cartItem) => ({
                    uid: Math.random(),
                    id: cartItem.id,
                    item: cartItem.item,
                    price: cartItem.sales_price,
                    qty: cartItem.quantity,
                    extra: cartItem.extra,
                  })),
              },
            ],
          });

          setCId(searchParams.get("c-id"));
          cIdRef.current = searchParams.get("c-id");
        })
        .catch((err) => { })
        .finally((f) => {
          setUpdateLoading(false);
        });

      return () => {
        cleanUp();
      };
    } else if (searchParams.get("d-id")) {
      if (id) {
        storeProps.setFormState({
          key: "update",
          value: true,
        });
      }

      const doc = JSON.parse(localStorage.getItem("sales") || "[]").find(
        (x) => x.dId == searchParams.get("d-id")
      );

      doc.details.forEach((detail) => {
        const item = detail.item;
        detail.item = appState.resources.items.find((x) => x.id == item);
        if (!detail.item) {
          toast.warning(
            <p>
              Item <b>{item.displayName}</b> can not be found. It is removed
              from your cart.
            </p>
          );
        } else if (!detail.item.active) {
          detail.locked = !!doc.id;
          toast.warning(
            <p>
              Item <b>{item.displayName}</b> is inactive. It is locked in your
              cart.
            </p>
          );
        }
      });

      storeProps.setFormState({
        batch: [
          {
            key: "info",
            value: {
              id,
              dId: searchParams.get("d-id"),
              salesDate: doc.docDate,
              deliveryDate: doc.deliveryDate,
              salesMan: doc.salesMan,
              customer: doc.customer && {
                value: doc.customer,
                label: doc.customer?.name,
              },
              customerBranch: doc.customerBranch && {
                value: doc.customerBranch,
                label: doc.customerBranch.name,
              },
              customerEmail: doc.customerEmail,
              customerPONumber: doc.customerOrderNumber,
              paymentTerms: doc.paymentTerms && {
                value: doc.paymentTerms,
                label: doc.paymentTerms.name,
              },
              customerSignature: doc.customerSignature,
              salesmanSignature: doc.salesManSignature,
              remarks: doc.remarks,
              terms: doc.terms,
              labeling: doc.labeling,
            },
          },
          {
            key: "details",
            value: doc.details
              .filter((x) => x.item)
              .map((cartItem) => ({
                uid: Math.random(),
                item: cartItem.item,
                price: cartItem.salesPrice || 0,
                qty: cartItem.quantity,
                extra: cartItem.extra,
                locked: cartItem.locked,
              })),
          },
        ],
      });
    } else if (id) {
      setUpdateLoading(true);
      storeProps.setFormState({
        key: "update",
        value: true,
      });

      apollo
        .query({
          query: updateData,
          variables: { id },
        })
        .then((data) => {
          const so = data.data.salesOrders[0];
          so.salesOrderDetails.forEach((detail) => {
            const item = detail.item;
            detail.item = appState.resources.items.find((x) => x.id == item.id);
            if (!detail.item) {
              toast.warning(
                <p>
                  Item <b>{item.displayName}</b> can not be found. It is removed
                  from your cart.
                </p>
              );
            } else if (!detail.item.active) {
              detail.locked = true;
              toast.warning(
                <p>
                  Item <b>{item.displayName}</b> is inactive. It is locked in
                  your cart.
                </p>
              );
            }
          });

          storeProps.setFormState({
            batch: [
              {
                key: "info",
                value: {
                  id,
                  salesDate: so.docDate,
                  deliveryDate: so.deliveryDate,
                  salesMan: so.salesMan,
                  customer: { value: so.customer, label: so.customer?.name },
                  customerBranch: {
                    value: so.customerBranch,
                    label: so.customerBranch?.name,
                  },
                  customerEmail: so.customerEmail,
                  customerPONumber: so.customerOrderNumber,
                  paymentTerms: {
                    value: appState.resources.paymentTerms.find(
                      (x) => x.id == so.paymentTerms?.id
                    ),
                    label: appState.resources.paymentTerms.find(
                      (x) => x.id == so.paymentTerms?.id
                    )?.name,
                  },
                  customerSignature: so.customerSignature,
                  salesmanSignature: so.salesManSignature,
                  remarks: so.remarks,
                  terms: so.terms,
                  labeling: so.labeling,
                },
              },
              {
                key: "details",
                value: so.salesOrderDetails
                  .filter((detail) => !!detail.item)
                  .map((cartItem) => ({
                    uid: Math.random(),
                    id: cartItem.id,
                    item: cartItem.item,
                    price: cartItem.salesPrice,
                    qty: cartItem.quantity,
                    extra: cartItem.extra,
                    locked: cartItem.locked,
                  })),
              },
            ],
          });
          const d = {
            info: {
              id,
              salesDate: so.docDate,
              deliveryDate: so.deliveryDate,
              salesMan: so.salesMan,
              customer: { value: so.customer, label: so.customer?.name },
              customerBranch: {
                value: so.customerBranch,
                label: so.customerBranch?.name,
              },
              customerEmail: so.customerEmail,
              customerPONumber: so.customerOrderNumber,
              paymentTerms: {
                value: appState.resources.paymentTerms.find(
                  (x) => x.id == so.paymentTerms.id
                ),
                label: appState.resources.paymentTerms.find(
                  (x) => x.id == so.paymentTerms.id
                )?.name,
              },
              customerSignature: so.customerSignature,
              salesmanSignature: so.salesManSignature,
              remarks: so.remarks,
              terms: so.terms,
              labeling: so.labeling,
            },
            details: so.salesOrderDetails
              .filter((detail) => !!detail.item)
              .map((cartItem) => ({
                uid: Math.random(),
                id: cartItem.id,
                item: cartItem.item,
                price: cartItem.salesPrice,
                qty: cartItem.quantity,
                extra: cartItem.extra,
                locked: cartItem.locked,
              })),
          };
          draftIt(d);
        })
        .catch((err) => { })
        .finally((f) => {
          setUpdateLoading(false);
        });
      return () => {
        cleanUp();
      };
    } else {
      draftIt();
      return () => {
        cleanUp();
      };
    }
  }, []);

  // fetching temp order which is the last openned order (not used or depracated)
  useEffect(() => {
    if (id || searchParams.get("d-id")) return;

    const tempOrder = JSON.parse(localStorage.getItem("temp-order") || "{}");
    if (tempOrder?.state?.details?.length) {
      draftIt({
        details: tempOrder.state.details.filter((x) => x.item),
        info: tempOrder.state.info,
      });
      localStorage.removeItem("temp-order");
    }
  }, []);

  // setting the latest resources if resources change
  useEffect(() => {
    if (!resourceLoading && resourceData) {
      const itemMasterData = humps.camelizeKeys(resourceData.itemMasterData);
      const { items, salesItems, purchaseItems, itemGroups, uoms } =
        makeItemMasterData(itemMasterData, appState.resources.taxes);
      setAppState({
        batch: [
          {
            key: "resources.items",
            value: items,
          },
          {
            key: "resources.salesItems",
            value: salesItems,
          },
          {
            key: "resources.purchaseItems",
            value: purchaseItems,
          },
          {
            key: "resources.itemGroups",
            value: itemGroups.filter((x) => x.active),
          },
          {
            key: "resources.uoms",
            value: uoms,
          },
        ],
      });
    }
  }, [resourceLoading]);

  // handling draft callback from server
  useEffect(() => {
    if (!loading && data && !searchParams.get("c-id")) {
      const uuid = data.data.data.uuid;
      setSearchParams({ "c-id": uuid });
      setCId(uuid);
      cIdRef.current = uuid;
    } else if (!loading && error) {
      setFailedCounter((failedCounter) => failedCounter + 1);
    }
  }, [loading]);

  // handle changes to failure count
  useEffect(() => {
    if (failedCounter === 0) return;

    if (failedCounter >= FAILURE_FATIGUE) {
      toast.error(
        "System turned into offline mode due to poor connection,you can change back to online mode manually. Your order is drafted locally."
      );
      setAppState({
        key: "settings.internet.online",
        value: false,
      });
      if (draftIntervalRef.current) clearInterval(draftIntervalRef.current);
      setDraftInterval(null);
      setCId(null);
      draftIntervalRef.current = null;
      draftIt(null, true);
      setFailedCounter(0);
    } else {
      setTimeout(() => {
        draftIt();
      }, RETRY_FAILURE_INTERVAL * 1000);
    }
  }, [failedCounter]);

  // update latest order with c-id
  useEffect(() => {
    if (cId && storeProps.formState && !draftInterval) {
      periodicDraft(storeProps.formState);
      window.addEventListener("beforeunload", cleanUp);
      localStorage.setItem("last-order", `c-id=${cId}`);
      setAppState({
        key: "lastOrder",
        value: `c-id=${cId}`,
      });
    }
  }, [cId, storeProps.formState]);

  // update latest order with e-id and draft on the spot
  useEffect(() => {
    if (eId && storeProps.formState) {
      draftIt(storeProps.formState, true);
      setAppState({
        key: "lastOrder",
        value: `e-id=${eId}`,
      });
    }
  }, [eId, storeProps.formState]);

  // keeping formState in ref
  useEffect(() => {
    formStateRef.current = storeProps.formState;
  }, [storeProps.formState]);

  // keeping draftInterval in ref
  useEffect(() => {
    draftIntervalRef.current = draftInterval;
  }, [draftInterval]);

  return (
    <React.Fragment>
      <FormProvider value={storeProps}>
        {(updateLoading || resourceLoading) && "Loading Store..."}
        {!updateLoading && !resourceLoading && <SalesOrderForm key={id} />}
      </FormProvider>
    </React.Fragment>
  );
};

export default Store;
